import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Brain,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Database,
  FileText,
  Scale,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { HealthScoreWidget } from '../../components/HealthScoreWidget';

const RISK_BADGE = {
  prohibited: { label: 'Proibido', color: 'bg-red-100 text-red-800 border-red-300' },
  high: { label: 'Alto Risco', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  limited: { label: 'Risco Limitado', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  minimal: { label: 'Risco Mínimo', color: 'bg-green-100 text-green-800 border-green-300' },
};

export default function AiActDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aiActApi.dashboardStats()
      .then(res => setStats(res.data))
      .catch(() => toast.error('Erro ao carregar dashboard EU AI Act'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout title="EU AI Act — Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  const score = stats?.global_score ?? 0;
  const scoreColor = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <Layout title="EU AI Act — Dashboard">
      <div className="space-y-6" data-testid="ai-act-dashboard">

        {/* AI Compliance Health Score */}
        <HealthScoreWidget />

        {/* Hero card with global score */}
        <Card className="card-flat bg-gradient-to-br from-indigo-50 via-white to-blue-50 border-indigo-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-indigo-600" />
                  <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Regulamento UE 2024/1689</span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">Conformidade EU AI Act</h2>
                <p className="text-sm text-slate-600 max-w-2xl">
                  Estado consolidado da conformidade dos seus sistemas IA com o Regulamento Europeu de Inteligência Artificial.
                </p>
              </div>
              <div className="text-center" data-testid="global-score-card">
                <div className={cn('text-5xl font-bold', scoreColor)}>{score}%</div>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Score Global</p>
                <Progress value={score} className="w-32 mt-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat" data-testid="kpi-total-systems">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Brain className="h-8 w-8 text-slate-400" />
                <span className="text-2xl font-bold text-slate-900">{stats?.total_systems ?? 0}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Sistemas IA</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-red-500" data-testid="kpi-high-risk">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ShieldAlert className="h-8 w-8 text-red-500" />
                <span className="text-2xl font-bold text-red-600">{stats?.high_risk ?? 0}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Alto Risco</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-orange-500" data-testid="kpi-pending-incidents">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold text-orange-600">{stats?.pending_notifications ?? 0}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Incidentes p/ Notificar</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-green-500" data-testid="kpi-conformant">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-2xl font-bold text-green-600">{stats?.conformant ?? 0}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Conformes</p>
            </CardContent>
          </Card>
        </div>

        {/* Risk distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-flat" data-testid="risk-distribution-card">
            <CardHeader>
              <CardTitle className="text-lg">Distribuição por Nível de Risco</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { key: 'prohibited', value: stats?.prohibited ?? 0 },
                  { key: 'high', value: stats?.high_risk ?? 0 },
                  { key: 'limited', value: stats?.limited ?? 0 },
                  { key: 'minimal', value: stats?.minimal ?? 0 },
                ].map(({ key, value }) => {
                  const cfg = RISK_BADGE[key];
                  const total = stats?.total_systems || 1;
                  const pct = Math.round((value / total) * 100);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <Badge variant="outline" className={cn('text-xs', cfg.color)}>{cfg.label}</Badge>
                        <span className="font-medium text-slate-700">{value} ({pct}%)</span>
                      </div>
                      <Progress value={pct} />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="card-flat" data-testid="gpai-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="h-5 w-5 text-indigo-600" />
                Modelos GPAI (Arts. 51-56)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="text-4xl font-bold text-indigo-600 mb-2">{stats?.gpai ?? 0}</div>
                <p className="text-sm text-slate-600 mb-4">
                  Modelos de IA de Propósito Geral registados
                </p>
                <Link to="/ai-act/gpai">
                  <Button variant="outline" size="sm" data-testid="goto-gpai-btn">
                    Gerir GPAI
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Per-system readiness */}
        <Card className="card-flat" data-testid="per-system-readiness">
          <CardHeader>
            <CardTitle className="text-lg">Estado por Sistema IA</CardTitle>
          </CardHeader>
          <CardContent>
            {(!stats?.per_system_conformity || stats.per_system_conformity.length === 0) ? (
              <div className="text-center py-8 text-slate-500">
                <Brain className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Sem sistemas IA registados</p>
                <Link to="/ai-act/systems">
                  <Button className="mt-4" data-testid="goto-systems-empty-btn">
                    Adicionar Sistema IA
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.per_system_conformity.map((s) => {
                  const riskCfg = RISK_BADGE[s.ai_risk_level] || { label: 'Não classificado', color: 'bg-slate-100 text-slate-700 border-slate-300' };
                  return (
                    <Link
                      key={s.system_id}
                      to={`/ai-act/systems/${s.system_id}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`system-row-${s.system_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Brain className="h-5 w-5 text-indigo-600 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{s.name}</p>
                          <p className="text-xs text-slate-500 font-mono">{s.system_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge variant="outline" className={cn('text-xs', riskCfg.color)}>{riskCfg.label}</Badge>
                        <div className="text-right w-24">
                          <p className="text-xs text-slate-500">Doc Técnica</p>
                          <Progress value={s.completeness} className="h-1.5 mt-1" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/ai-act/systems" data-testid="quick-systems">
            <Card className="card-flat hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <Brain className="h-6 w-6 text-indigo-600" />
                <div><p className="font-medium">Inventário Sistemas IA</p><p className="text-xs text-slate-500">F1 — Inventário e classificação</p></div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/ai-act/risks" data-testid="quick-risks">
            <Card className="card-flat hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <ShieldAlert className="h-6 w-6 text-red-600" />
                <div><p className="font-medium">Riscos IA</p><p className="text-xs text-slate-500">F3 — Art. 9 Gestão de risco</p></div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/ai-act/incidents" data-testid="quick-incidents">
            <Card className="card-flat hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
                <div><p className="font-medium">Incidentes IA</p><p className="text-xs text-slate-500">F11 — Art. 72 Notificação 15 dias</p></div>
              </CardContent>
            </Card>
          </Link>
        </div>

      </div>
    </Layout>
  );
}
